import { useState } from 'react'
import { retryPost, recoverApprovalCreate, type PostProcessingState, type PostProcessingTask } from '../../api/hub/collaboration'
import { MutationState, useHubMutation } from '../focus/shared'

const taskLabels: Record<string, string> = { enrichment: 'AI 整理要点', notification: '读者通知', inbox: '收件箱记录', proposal_approval: '立项审批', broadcast: '必读广播' }
const statusLabels: Record<string, string> = { pending: '等待处理', done: '已完成', failed: '自动尝试已停止', needs_review: '结果未知，待核查' }
function ApprovalRecovery({ task }: { task: PostProcessingTask }) {
  const [evidence, setEvidence] = useState(''), [outcome, setOutcome] = useState<'occurred' | 'not_occurred'>('occurred')
  const recovery = task.recovery!
  const verify = useHubMutation((body: { mutationKey: string; outcome: 'occurred' | 'not_occurred'; evidence: string }) => recoverApprovalCreate(body))
  return <form className="lc-form" onSubmit={e => { e.preventDefault(); verify.mutate({ mutationKey: recovery.mutationKey, outcome, evidence: evidence.trim() }); }}>
    <p>原审批编号：{recovery.approvalId}。核验只针对原请求；不会新发审批或重新生成提案。</p>
    <blockquote>{recovery.originalProposal}</blockquote>
    <label>核查结论<select value={outcome} onChange={e => setOutcome(e.target.value as 'occurred' | 'not_occurred')}><option value="occurred">读取原审批并核验已发生</option><option value="not_occurred">核验原审批未发生</option></select></label>
    <p>服务端会读取原 UUID 并比对原表单；“未发生”须取得权威不存在回执，不能仅凭未找到就重发。核验完成后仍须明确继续原处理。</p>
    <label>核查依据<textarea required maxLength={5000} value={evidence} onChange={e => setEvidence(e.target.value)}/></label>
    <button disabled={verify.isPending || !evidence.trim()}>按原键核验审批结果</button>
    {verify.isSuccess && <p role="status">核验记录已提交。请核对更新后的状态，再继续未完成处理。</p>}
    <MutationState mutation={verify}/>
  </form>
}
export function PostProcessing({ id, processing, canRecover }: { id: string; processing?: PostProcessingState; canRecover: boolean }) {
  const retry = useHubMutation(() => retryPost(id))
  const tasks = processing?.tasks ?? []
  if (!processing || (!tasks.some(t => t.status !== 'done') && !processing.errors.length)) return null
  const retryable = tasks.some(t => ['pending', 'failed'].includes(t.status) || (t.status === 'needs_review' && t.recovery && ['ready', 'done'].includes(t.recovery.status)))
  return <section aria-label="分享处理与恢复" className="lc-row">
    <h3>分享处理与恢复</h3>
    <p>完成项保留，不重复执行。继续处理会复用已记录的生成结果与原审批请求；结果未知的审批须先按原键核验。</p>
    {tasks.map(task => <article key={task.taskKey}>
      <h4>{Object.hasOwn(taskLabels, task.kind) ? taskLabels[task.kind] : '其他处理'} · {statusLabels[task.status] ?? '待核对状态'}</h4>
      <p>本轮尝试 {task.attempts} / {task.maxAttempts}{task.nextRetryAt ? ` · 下次自动尝试 ${new Date(task.nextRetryAt).toLocaleString('zh-CN')}` : ''}</p>
      {['enrichment', 'proposal_approval'].includes(task.kind) && <p>{task.phase === 'apply' ? '已有生成结果，等待应用' : task.phase === 'approval' ? '沿用原审批请求继续处理' : '等待生成材料'}</p>}
      {task.error && <p>{task.error}</p>}
      {task.status === 'needs_review' && <p>外部结果仍未知，不能盲目重发。请核对原记录。</p>}
      {canRecover && task.kind === 'proposal_approval' && task.recovery && ['sending', 'needs_review'].includes(task.recovery.status) && <ApprovalRecovery task={task}/>}
    </article>)}
    {!tasks.length && processing.errors.map((error, i) => <p key={i}>{error.message}</p>)}
    {canRecover ? retryable && <button disabled={retry.isPending} onClick={() => retry.mutate()}>继续未完成处理</button> : <p>等待作者或 CEO 核对并恢复。</p>}
    {retry.isSuccess && <p role="status">已请求继续原处理，请以更新后的各项状态为准。</p>}
    <MutationState mutation={retry}/>
  </section>
}
